import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { news } from '@/mocks/news';
import { formatDate, getMovieById, getNewsById } from '@/utils/media';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 5;

export default function News() {
  usePageMeta({
    title: '河北彩花动态资讯 - 作品资讯与明星动态',
    description:
      '汇总河北彩花相关的作品资讯与明星动态，可按类别筛选并按时间排序，点击查看详细内容。',
    keywords: '河北彩花动态,河北彩花资讯,作品资讯,明星动态',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState<'全部' | '作品资讯' | '明星动态'>('全部');
  const [sortKey, setSortKey] = useState<'latest' | 'earliest'>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && getNewsById(openId)) {
      setActiveId(openId);
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    const list = news.filter((item) => category === '全部' || item.category === category);
    return [...list].sort((a, b) =>
      sortKey === 'latest' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [category, sortKey]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [category, sortKey]);

  const shown = filtered.slice(0, visible);
  const activeNews = activeId ? getNewsById(activeId) : null;
  const activeMovie = activeNews?.relatedMovie ? getMovieById(activeNews.relatedMovie) : null;

  const closeModal = () => {
    setActiveId(null);
    if (searchParams.get('open')) {
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '动态资讯' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            河北彩花动态资讯 - 作品资讯与明星动态
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            汇总河北彩花相关的最新活动、作品资讯与明星动态，可按类别筛选，点击标题查看详细内容。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
            {(['全部', '作品资讯', '明星动态'] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  category === item ? 'bg-background-50 text-primary-700' : 'text-foreground-500 hover:text-foreground-900'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-foreground-500">排序：</span>
            <select
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value as 'latest' | 'earliest')}
              className="rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="latest">最新发布</option>
              <option value="earliest">最早发布</option>
            </select>
          </div>
        </div>

        {shown.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {shown.map((item) => (
                <article
                  key={item.id}
                  className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        item.category === '作品资讯'
                          ? 'bg-primary-50 text-primary-700'
                          : 'bg-accent-100 text-accent-900'
                      }`}
                    >
                      {item.category}
                    </span>
                    <span className="text-xs text-foreground-400">{formatDate(item.date)}</span>
                  </div>
                  <h2 className="mt-3">
                    <button
                      type="button"
                      onClick={() => setActiveId(item.id)}
                      className="text-left font-heading text-base font-bold text-foreground-950 transition-colors hover:text-primary-600 md:text-lg"
                    >
                      {item.title}
                    </button>
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setActiveId(item.id)}
                      className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line"></i>
                    </button>
                    {item.relatedMovie && (
                      <Link
                        to={`/movie/${item.relatedMovie}`}
                        className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-foreground-900"
                      >
                        <i className="ri-film-line"></i>
                        相关作品
                      </Link>
                    )}
                    {item.relatedStar && (
                      <Link
                        to={`/star/${item.relatedStar}`}
                        className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-foreground-900"
                      >
                        <i className="ri-user-line"></i>
                        相关明星
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>

            {visible < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-add-line"></i>
                  加载更多（剩余 {filtered.length - visible} 条）
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-article-line text-2xl"></i>
            </div>
            <p className="mt-4 text-base font-medium text-foreground-800">暂无相关资讯内容</p>
            <p className="mt-2 text-sm text-foreground-500">换个类别看看，或稍后再来</p>
          </div>
        )}
      </div>

      {activeNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={closeModal} role="presentation"></div>
          <div className="relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-background-50 p-6 md:p-8">
            <button
              type="button"
              aria-label="关闭"
              onClick={closeModal}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="flex flex-wrap items-center gap-3 pr-10">
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  activeNews.category === '作品资讯'
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-accent-100 text-accent-900'
                }`}
              >
                {activeNews.category}
              </span>
              <span className="text-xs text-foreground-400">{formatDate(activeNews.date)}</span>
            </div>
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {activeNews.title}
            </h2>
            <p className="mt-3 rounded-md bg-background-100 p-3 text-sm leading-relaxed text-foreground-600">
              {activeNews.summary}
            </p>
            <div className="mt-5 flex flex-col gap-4">
              {activeNews.content.split('\n').filter(Boolean).map((paragraph, index) => (
                <p key={index} className="text-sm leading-loose text-foreground-700">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3 border-t border-background-200 pt-5">
              {activeMovie && (
                <Link
                  to={`/movie/${activeMovie.id}`}
                  onClick={closeModal}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-film-line"></i>
                  查看相关作品《{activeMovie.title}》
                </Link>
              )}
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}